import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import type { DpPayrollItemKind, PayrollMonthInputPayload } from "../api/payrollApi";
import { NOTICE_TYPE_LABELS, TERMINATION_TYPE_LABELS } from "../api/processesApi";
import {
  calculateTerminationPaymentApi,
  closeTerminationPaymentApi,
  DpTerminationPayment,
  getTerminationMonthContextApi,
  getTerminationPaymentApi,
  getTerminationPaymentPdfApi,
  reopenTerminationPaymentApi,
  terminationPaymentBadge,
  updateTerminationPaymentApi,
} from "../api/terminationApi";
import { formatCurrency, formatDateOnly, toDateInputValue } from "../utils/format";
import DocumentFileButton from "./DocumentFileButton";
import MonthInputModal from "./MonthInputModal";
import ReasonModal from "./ReasonModal";
import FinanceBridgeBox from "./FinanceBridgeBox";

type Props = {
  token: string;
  contractId: string;
  employeeName: string;
};

const SECTIONS: Array<{ kind: DpPayrollItemKind; title: string }> = [
  { kind: "PROVENTO", title: "Proventos" },
  { kind: "DESCONTO", title: "Descontos" },
  { kind: "INFORMATIVO", title: "Informativos (não entram no líquido)" },
];

const formatReference = (value: string | null) =>
  value === null ? "—" : Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 2 });

const formatDateTime = (value: string | null) =>
  value ? new Date(value).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" }) : "";

const days = (value: number) => value.toLocaleString("pt-BR", { maximumFractionDigits: 2 });

/** "1 falta · 2,5 h HE 50%" — resumo da apuração gravada. */
const describeManual = (m: NonNullable<DpTerminationPayment["manualInput"]>) => {
  const h = (v: string | null) => (v === null ? null : Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 2 }));
  const parts = [
    m.workedDays !== null ? `${m.workedDays} dias trabalhados` : null,
    m.absenceDays ? `${m.absenceDays} falta(s)` : null,
    m.dsrLostCount ? `${m.dsrLostCount} DSR perdido(s)` : null,
    h(m.overtimeWorkdayHours) ? `${h(m.overtimeWorkdayHours)} h HE 50%` : null,
    h(m.overtimeRestDayHours) ? `${h(m.overtimeRestDayHours)} h HE 100%` : null,
    h(m.nightHours) ? `${h(m.nightHours)} h noturnas` : null,
    m.lateMinutes ? `${m.lateMinutes} min de atraso` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "sem ocorrências";
};

/**
 * Demonstrativo de rescisão de conferência (F2c): prévia ou demonstrativo gravado,
 * com o resumo das verbas, as linhas e a memória de cálculo, a data de pagamento e o
 * saldo do FGTS editáveis enquanto aberto, a apuração do mês do afastamento (quando o
 * espelho não está fechado), Calcular, Fechar, Reabrir (com motivo) e o PDF.
 */
export default function TerminationPaymentPanel({ token, contractId, employeeName }: Props) {
  const [payment, setPayment] = useState<DpTerminationPayment | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [paymentDate, setPaymentDate] = useState("");
  const [fgtsBalance, setFgtsBalance] = useState("");
  const [reopening, setReopening] = useState(false);
  const [monthInputOpen, setMonthInputOpen] = useState(false);

  const apply = useCallback((data: DpTerminationPayment) => {
    setPayment(data);
    setPaymentDate(toDateInputValue(data.paymentDate));
    setFgtsBalance(data.fgtsBalance === null ? "" : String(Number(data.fgtsBalance)));
  }, []);

  useEffect(() => {
    getTerminationPaymentApi(token, contractId)
      .then(apply)
      .catch((error: any) => toast.error(error?.message || "Erro ao carregar o demonstrativo de rescisão."))
      .finally(() => setLoading(false));
  }, [token, contractId, apply]);

  const run = async (action: () => Promise<DpTerminationPayment>, success: string) => {
    setBusy(true);
    try {
      apply(await action());
      toast.success(success);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar o demonstrativo.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="dpEmpty">Carregando...</div>;
  if (!payment) return <div className="dpEmpty">Demonstrativo indisponível.</div>;

  const closed = payment.status === "FECHADO";
  const pending = payment.resultStatus === "PENDENTE";
  const inProgress = payment.termination.source === "PROCESSO";
  const savedBalance = payment.fgtsBalance === null ? "" : String(Number(payment.fgtsBalance));
  const changed = paymentDate !== toDateInputValue(payment.paymentDate) || fgtsBalance !== savedBalance;
  const badge = terminationPaymentBadge(payment);
  const s = payment.summary;
  const projected = s.terminationDate !== s.separationDate;

  const saveFields = () =>
    run(
      () =>
        updateTerminationPaymentApi(token, contractId, {
          paymentDate: paymentDate || undefined,
          fgtsBalance: fgtsBalance.trim() === "" ? null : Number(fgtsBalance),
        }),
      "Demonstrativo salvo e recalculado."
    );

  return (
    <>
      <p className="dpNotice">
        Demonstrativo de <strong>conferência</strong>: não substitui o termo de rescisão (TRCT) do contador nem o eSocial.{" "}
        {payment.saved
          ? "Recalcule depois de mudar o ponto, os lançamentos ou o desligamento."
          : "Esta é uma prévia calculada agora; nada foi gravado. Clique em Calcular para gravar."}{" "}
        <span className={`dpBadge ${badge.className}`}>{badge.label}</span>
      </p>
      {inProgress ? (
        <p className="dpNotice dpNotice--warning">
          Desligamento em andamento: os dados vêm do processo. O demonstrativo só fecha depois de concluir o desligamento.
        </p>
      ) : null}

      {payment.warnings.length ? (
        <div className={`dpNotice ${pending ? "dpNotice--warning" : ""}`}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {payment.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <FinanceBridgeBox
        view={payment.financeiro}
        hint="Calcular, salvar e Fechar atualizam a previsão no Financeiro (o líquido e, à parte, a multa do FGTS); Reabrir ou cancelar o desligamento cancela o que não tem baixa."
      />

      <div className="dpDefinitionGrid" style={{ marginBottom: 16 }}>
        <Field label="Desligamento" value={TERMINATION_TYPE_LABELS[s.terminationType]} />
        <Field
          label="Aviso prévio"
          value={
            s.noticeDays
              ? `${NOTICE_TYPE_LABELS[s.noticeType]} · ${s.noticeDays} dias` +
                (s.noticePaidDays !== null && s.noticePaidDays !== s.noticeDays ? ` (${days(s.noticePaidDays)} pagos)` : "")
              : NOTICE_TYPE_LABELS[s.noticeType]
          }
        />
        <Field label="Afastamento (fim do saldo)" value={formatDateOnly(s.separationDate)} />
        <Field label={projected ? "Término com a projeção do aviso" : "Término"} value={formatDateOnly(s.terminationDate)} />
        <Field label="Prazo de pagamento (art. 477)" value={formatDateOnly(s.paymentDeadline)} />
        <Field
          label="13º (avos)"
          value={
            s.thirteenthAvos === null
              ? "Não devido"
              : `${s.thirteenthAvos}/12` + (s.thirteenthNoticeAvos ? ` + ${s.thirteenthNoticeAvos}/12 do aviso` : "")
          }
        />
        <Field
          label="Férias indenizadas (dias)"
          value={
            [
              s.vacationDays.overdue ? `${days(s.vacationDays.overdue)} vencidas` : null,
              s.vacationDays.doubled ? `${days(s.vacationDays.doubled)} em dobro` : null,
              s.vacationDays.proportional ? `${days(s.vacationDays.proportional)} proporcionais` : null,
              s.vacationDays.notice ? `${days(s.vacationDays.notice)} do aviso` : null,
            ]
              .filter(Boolean)
              .join(" · ") || "—"
          }
        />
        {s.remainingTermDays !== null ? <Field label="Dias até o fim do prazo" value={String(s.remainingTermDays)} /> : null}
        <Field
          label="Multa do FGTS (guia)"
          value={s.fgtsFinePct ? `${s.fgtsFinePct}% · ${s.fgtsFine ? formatCurrency(s.fgtsFine) : "—"}` : "Não devida"}
        />
      </div>

      <div className="dpFormGrid">
        <div className="dpFormGroup">
          <label htmlFor={`termPayDate-${contractId}`}>Data de pagamento</label>
          <input
            id={`termPayDate-${contractId}`}
            type="date"
            className="dpInput"
            value={paymentDate}
            disabled={busy || closed}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
          <span className="dpFormHint">
            Até 10 dias do afastamento (art. 477 §6º). Decide a tabela do IR.
            {payment.suggestedPaymentDate ? ` Sugerida: ${formatDateOnly(payment.suggestedPaymentDate)}.` : ""}
          </span>
        </div>
        <div className="dpFormGroup">
          <label htmlFor={`termFgts-${contractId}`}>Saldo do FGTS para fins rescisórios</label>
          <input
            id={`termFgts-${contractId}`}
            type="number"
            min="0"
            step="0.01"
            className="dpInput"
            placeholder="Extrato do FGTS Digital"
            value={fgtsBalance}
            disabled={busy || closed}
            onChange={(e) => setFgtsBalance(e.target.value)}
          />
          <span className="dpFormHint">Base da multa (informativa, paga por guia), com o FGTS desta rescisão.</span>
        </div>
        <div className="dpFormGroup">
          {changed && !closed ? (
            <button
              type="button"
              className="dpBtn dpBtn--secondary"
              style={{ marginTop: 24 }}
              disabled={busy}
              onClick={saveFields}
            >
              Salvar e recalcular
            </button>
          ) : null}
        </div>
      </div>

      <div className="dpNotice">
        <strong>Mês do afastamento: </strong>
        {payment.timesheetClosed ? (
          "espelho de ponto fechado — faltas e horas extras vêm do ponto."
        ) : (
          <>
            {payment.manualInput ? `apuração informada (${describeManual(payment.manualInput)}).` : "sem espelho de ponto fechado nem apuração."}{" "}
            {!closed ? (
              <button
                type="button"
                className="dpBtn dpBtn--secondary dpBtn--small"
                disabled={busy}
                onClick={() => setMonthInputOpen(true)}
              >
                {payment.manualInput ? "Corrigir apuração" : "Apurar mês"}
              </button>
            ) : null}
          </>
        )}
      </div>

      <div className="dpCounters">
        {[
          ["Bruto", formatCurrency(payment.grossAmount)],
          ["Descontos", formatCurrency(payment.deductionsAmount)],
          ["Líquido", formatCurrency(payment.netAmount)],
          ["FGTS (informativo)", formatCurrency(payment.fgtsAmount)],
        ].map(([label, value]) => (
          <div key={label} className="dpCounter">
            <div className="dpCounter__value">{value}</div>
            <div className="dpCounter__label">{label}</div>
          </div>
        ))}
      </div>

      {SECTIONS.map(({ kind, title }) => {
        const lines = payment.lines.filter((l) => l.kind === kind);
        if (lines.length === 0) return null;
        return (
          <section key={kind} className="dpSection">
            <h3 className="dpSection__title">{title}</h3>
            <div className="dpTableWrapper">
              <table className="dpTable">
                <thead>
                  <tr>
                    <th>Rubrica</th>
                    <th>Referência</th>
                    <th>Valor</th>
                    <th>Como foi calculado</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.id}>
                      <td>
                        <strong>{l.itemName}</strong>
                        {l.externalCode ? <div className="dpFormHint">Código {l.externalCode}</div> : null}
                      </td>
                      <td>{formatReference(l.reference)}</td>
                      <td>{formatCurrency(l.amount)}</td>
                      <td className="dpFormHint" style={{ whiteSpace: "normal" }}>
                        {l.memo}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      {payment.closedAt ? (
        <p className="dpFormHint">
          Fechado {payment.closedBy ? `por ${payment.closedBy.nomeCompleto} ` : ""}em {formatDateTime(payment.closedAt)}.
        </p>
      ) : null}
      {payment.reopenedAt ? (
        <p className="dpFormHint">
          Reaberto {payment.reopenedBy ? `por ${payment.reopenedBy.nomeCompleto} ` : ""}em {formatDateTime(payment.reopenedAt)}
          {payment.reopenReason ? `: ${payment.reopenReason}` : ""}.
        </p>
      ) : null}

      <div className="dpHeader__actions" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
        {payment.saved ? (
          <DocumentFileButton
            token={token}
            fetchBlob={() => getTerminationPaymentPdfApi(token, contractId)}
            fileName={`rescisao-${payment.termination.separationDate.slice(0, 10)}.pdf`}
            label="PDF"
            className="dpBtn dpBtn--secondary"
            disabled={busy || pending}
            title={
              pending
                ? "Resolva as pendências e recalcule para imprimir."
                : closed
                  ? "Abrir o demonstrativo para imprimir"
                  : "Abrir o demonstrativo (prévia: rescisão aberta)"
            }
          />
        ) : null}
        {closed ? (
          <button type="button" className="dpBtn dpBtn--secondary" disabled={busy} onClick={() => setReopening(true)}>
            Reabrir rescisão
          </button>
        ) : (
          <>
            <button
              type="button"
              className="dpBtn dpBtn--secondary"
              disabled={busy}
              onClick={() => run(() => calculateTerminationPaymentApi(token, contractId), "Rescisão calculada.")}
            >
              Calcular
            </button>
            <button
              type="button"
              className="dpBtn dpBtn--primary"
              disabled={busy || changed || inProgress}
              title={
                inProgress
                  ? "Conclua o desligamento antes de fechar."
                  : changed
                    ? "Salve a data e o saldo do FGTS antes de fechar."
                    : "Recalcula e congela."
              }
              onClick={() => run(() => closeTerminationPaymentApi(token, contractId), "Rescisão fechada.")}
            >
              Fechar rescisão
            </button>
          </>
        )}
      </div>

      {monthInputOpen ? (
        <MonthInputModal
          token={token}
          employeeName={employeeName}
          current={payment.manualInput}
          source={{
            loadContext: () => getTerminationMonthContextApi(token, contractId),
            save: async (manualInput: PayrollMonthInputPayload) => {
              apply(await updateTerminationPaymentApi(token, contractId, { manualInput }));
            },
            remove: async () => {
              apply(await updateTerminationPaymentApi(token, contractId, { manualInput: null }));
            },
            savedMessage: "Apuração salva e rescisão recalculada.",
          }}
          onClose={() => setMonthInputOpen(false)}
          onSaved={() => setMonthInputOpen(false)}
        />
      ) : null}

      {reopening ? (
        <ReasonModal
          title="Reabrir demonstrativo de rescisão"
          description="O demonstrativo volta a ser prévia até ser fechado de novo."
          confirmLabel="Reabrir"
          onClose={() => setReopening(false)}
          onConfirm={async (reason) => {
            apply(await reopenTerminationPaymentApi(token, contractId, reason));
            setReopening(false);
            toast.success("Rescisão reaberta.");
          }}
        />
      ) : null}
    </>
  );
}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div className="dpDefinition__label">{label}</div>
    <div className="dpDefinition__value">{value}</div>
  </div>
);
